import React, { useEffect, useState } from 'react'
import { useRouteMatch } from 'react-router'

import {
  isArchiveRoute,
  isDraftsRoute,
  isProjectTrashRoute,
  isTrashRoute,
  RouteParams,
} from '@sketch/modules-common'

import { Button, Transition, useModalContext } from '@sketch/components'

import { useSelectionContext } from '../SelectionContext/SelectionContext'
import {
  Wrapper,
  Text,
  ActionButton,
  StyledDivider,
  DocumentIcon,
  ActionButtonWrapper,
  SelectionBottomSinkWrapper,
} from './SelectionBottomSink.styles'

import { ReactComponent as ArrowsRightLeftIcon } from '@sketch/icons/img-arrows-right-left-17'
import { ReactComponent as BoxIcon } from '@sketch/icons/img-box-17'
import { ReactComponent as TrashEmptyIcon } from '@sketch/icons/trash-empty-17'

import { useStorageItemsCount } from '../../hooks/useStorageItemsCount'

import {
  ProjectDetailsFragment,
  ProjectFragment,
  ShareListItemFragment,
  useGetWorkspaceQuery,
} from '@sketch/gql-types'
import {
  ArchiveStorageItemsModal,
  DeleteStorageItemsModal,
  MoveStorageItemModal,
} from '../../modals'
import { SelectedItemsCommandBarRegistration } from '../../commandBarCommands'
import { useGetProjects } from '../../../projects/operations'

type Maybe<T> = T | null | undefined

type StorageItem =
  | Pick<ShareListItemFragment, 'identifier' | '__typename'>
  | Pick<ProjectFragment, 'identifier' | '__typename'>

interface BaseSelectionBottomSinkProps {
  className?: string
  workspaceIdentifier: string
  project?: ProjectDetailsFragment
  selectedItems: StorageItem[]
  onClearSelection: () => void
  actions: SelectionBottomSinkActions
}

interface SelectionBottomSinkActions {
  isArchiveButtonAvailable: boolean
  isMoveButtonAvailable: boolean
  isRestoreButtonAvailable: boolean
  isTrashButtonAvailable: boolean
  isUnarchiveButtonAvailable: boolean
  parentProjectIdentifier: string | null
}

interface UseSelectionBottomSinkActionsProps {
  project?: ProjectDetailsFragment
  workspaceIdentifier: string
}

const useSelectionBottomSinkActions = ({
  project,
  workspaceIdentifier,
}: UseSelectionBottomSinkActionsProps): SelectionBottomSinkActions => {
  const { path, params } = useRouteMatch()

  const { data: workspaceData } = useGetWorkspaceQuery({
    variables: { identifier: workspaceIdentifier },
    fetchPolicy: 'cache-only',
  })

  const isArchiveFeatureEnabled =
    workspaceData?.workspace?.features?.archiveEnabled

  const isPathDraftsRoute = isDraftsRoute(path)
  const isPathTrashRoute = isTrashRoute(path) || isProjectTrashRoute(path)
  const isPathArchiveRoute = isArchiveRoute(path)
  const projects = useGetProjects({
    workspaceId: workspaceIdentifier,
    skip: !isPathDraftsRoute,
  })

  const parentProjectIdentifier = isPathDraftsRoute
    ? projects.draftsProject?.entries[0].identifier || null
    : (params as Maybe<RouteParams<'WORKSPACE_PROJECT'>>)?.projectId || null

  return {
    /**
     * We wanna show the archive button for all views except:
     * - When the user is in the archive route
     * - When the user is in the trash route
     * - When the project is already archived
     * - When the project is deleted
     */
    isArchiveButtonAvailable:
      !!isArchiveFeatureEnabled &&
      !isPathArchiveRoute &&
      !isPathTrashRoute &&
      !project?.archivedAt &&
      !project?.deletedAt,

    /**
     * We wanna show the move button for all views except:
     * - When the user is in the trash route
     * - When the user is in the archive route
     * - When the project is deleted
     * - When the project is archived
     */
    isMoveButtonAvailable:
      !isPathTrashRoute &&
      !isPathArchiveRoute &&
      !project?.deletedAt &&
      !project?.archivedAt,

    /**
     * We wanna show the restore from trash button only when:
     * - The user is in the trash route
     * - The project is deleted
     */
    isRestoreButtonAvailable: isPathTrashRoute || !!project?.deletedAt,

    /**
     * We wanna show the trash button for all views except:
     * - When the user is in the trash route
     * - When the project is already deleted
     */
    isTrashButtonAvailable: !isPathTrashRoute && !project?.deletedAt,

    /**
     * We wanna show the unarchive button only when:
     * - The user is in the archive route
     * - The project is archived
     */
    isUnarchiveButtonAvailable:
      !!isArchiveFeatureEnabled &&
      (isPathArchiveRoute || !!project?.archivedAt),

    parentProjectIdentifier,
  }
}

const BaseSelectionBottomSink = (props: BaseSelectionBottomSinkProps) => {
  const {
    actions,
    selectedItems,
    className,
    onClearSelection,
    workspaceIdentifier,
  } = props
  const {
    isArchiveButtonAvailable,
    isMoveButtonAvailable,
    isRestoreButtonAvailable,
    isTrashButtonAvailable,
    isUnarchiveButtonAvailable,
    parentProjectIdentifier,
  } = actions

  const { message } = useStorageItemsCount(selectedItems)
  const { showModal } = useModalContext()

  if (
    !(
      isUnarchiveButtonAvailable ||
      isArchiveButtonAvailable ||
      isTrashButtonAvailable ||
      isMoveButtonAvailable ||
      isRestoreButtonAvailable
    )
  ) {
    // No actions available, no need to show anything
    return null
  }

  return (
    <Wrapper className={className}>
      <DocumentIcon />
      <Text textStyle="copy.primary.standard.E" as="span">
        {message} selected
      </Text>

      <ActionButtonWrapper>
        {isMoveButtonAvailable && (
          <ActionButton
            description="Move..."
            onClick={() => {
              showModal(MoveStorageItemModal, {
                items: selectedItems,
                workspaceIdentifier,
                parentProjectIdentifier,
                action: 'move',
                originType: 'standard',
                onMove: onClearSelection,
              })
            }}
          >
            <ArrowsRightLeftIcon role="img" />
          </ActionButton>
        )}

        {isUnarchiveButtonAvailable && (
          <ActionButton
            description="Unarchive..."
            onClick={() => {
              showModal(MoveStorageItemModal, {
                items: selectedItems,
                workspaceIdentifier,
                parentProjectIdentifier,
                action: 'unarchive',
                originType: 'stashed',
                onMove: onClearSelection,
              })
            }}
          >
            <ArrowsRightLeftIcon role="img" />
          </ActionButton>
        )}

        {isRestoreButtonAvailable && (
          <ActionButton
            description="Restore..."
            onClick={() => {
              showModal(MoveStorageItemModal, {
                items: selectedItems,
                workspaceIdentifier,
                parentProjectIdentifier,
                action: 'restore',
                originType: 'stashed',
                onMove: onClearSelection,
              })
            }}
          >
            <ArrowsRightLeftIcon role="img" />
          </ActionButton>
        )}

        {isArchiveButtonAvailable && (
          <ActionButton
            description="Archive..."
            onClick={() =>
              showModal(ArchiveStorageItemsModal, {
                items: selectedItems,
                onArchive: onClearSelection,
              })
            }
          >
            <BoxIcon role="img" />
          </ActionButton>
        )}

        {isTrashButtonAvailable && (
          <ActionButton
            description="Delete..."
            onClick={() => {
              showModal(DeleteStorageItemsModal, {
                items: selectedItems,
                workspaceId: workspaceIdentifier,
                onDelete: onClearSelection,
              })
            }}
          >
            <TrashEmptyIcon role="img" />
          </ActionButton>
        )}
      </ActionButtonWrapper>

      <StyledDivider />

      <Button variant="tertiary" size="32" onClick={onClearSelection}>
        Clear
      </Button>
    </Wrapper>
  )
}

interface SelectionBottomSinkProps {
  className?: string
  workspaceIdentifier: string
  project?: ProjectDetailsFragment
}

export const SelectionBottomSink = (props: SelectionBottomSinkProps) => {
  const { className, workspaceIdentifier, project } = props

  const { selectedItems, clearSelection } = useSelectionContext()
  const actions = useSelectionBottomSinkActions({
    project,
    workspaceIdentifier,
  })

  return (
    <BaseSelectionBottomSink
      actions={actions}
      className={className}
      workspaceIdentifier={workspaceIdentifier}
      selectedItems={selectedItems}
      onClearSelection={clearSelection}
    />
  )
}

export const AnimatedSelectionBottomSink = (
  props: SelectionBottomSinkProps
) => {
  const { className, workspaceIdentifier, project } = props

  const { selectedItems, clearSelection, hasSelectedItems } =
    useSelectionContext()
  const actions = useSelectionBottomSinkActions({
    project,
    workspaceIdentifier,
  })

  const [cachedSelectedItems, setCachedSelectedItems] = useState(selectedItems)

  useEffect(() => {
    if (hasSelectedItems) {
      setCachedSelectedItems(selectedItems)
    }
  }, [hasSelectedItems, selectedItems])

  return (
    <>
      <SelectedItemsCommandBarRegistration
        workspaceIdentifier={workspaceIdentifier}
        selectedItems={selectedItems}
        onClearSelection={clearSelection}
        actions={actions}
      />
      <Transition
        timeout={300}
        in={hasSelectedItems}
        mountOnEnter
        unmountOnExit
      >
        {state => (
          <SelectionBottomSinkWrapper data-animation-state={state}>
            <BaseSelectionBottomSink
              actions={actions}
              className={className}
              workspaceIdentifier={workspaceIdentifier}
              selectedItems={cachedSelectedItems}
              onClearSelection={clearSelection}
            />
          </SelectionBottomSinkWrapper>
        )}
      </Transition>
    </>
  )
}
